import React, { useCallback } from 'react';
import { Linking, StyleSheet, TouchableOpacity } from 'react-native';

import { Stack } from '@/components/layout/stack';
import { SvgIcon, Text } from '@/components/ui';
import { showInfoAlert } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

type PlayerProfileOverviewContentProps = {
  parentName: string;
  phone: string;
  email: string;
};

export function PlayerProfileOverviewContent({
  parentName,
  phone,
  email,
}: PlayerProfileOverviewContentProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const sanitizeValue = useCallback((value: string) => value.trim(), []);

  const handleCall = useCallback(async () => {
    const number = sanitizeValue(phone);
    if (!number || number === '--') {
      showInfoAlert(STRINGS.common.featureUnderProgress);
      return;
    }

    await Linking.openURL(`tel:${number}`);
  }, [phone, sanitizeValue]);

  const handleText = useCallback(async () => {
    const number = sanitizeValue(phone);
    if (!number || number === '--') {
      showInfoAlert(STRINGS.common.featureUnderProgress);
      return;
    }

    await Linking.openURL(`sms:${number}`);
  }, [phone, sanitizeValue]);

  const handleEmail = useCallback(async () => {
    const currentEmail = sanitizeValue(email);
    if (!currentEmail || currentEmail === '--') {
      showInfoAlert(STRINGS.common.featureUnderProgress);
      return;
    }

    await Linking.openURL(`mailto:${currentEmail}`);
  }, [email, sanitizeValue]);

  return (
    <Stack flex={1} backgroundColor={'white'}>
      <Stack direction="row" gap={scale(40)} style={styles.actionsRow}>
        <ActionIcon
          label={STRINGS.player.profile.overview.actions.call}
          icon="IcCall"
          onPress={handleCall}
        />
        <ActionIcon
          label={STRINGS.player.profile.overview.actions.text}
          icon="IcText"
          onPress={handleText}
        />
        <ActionIcon
          label={STRINGS.player.profile.overview.actions.email}
          icon="IcMail"
          onPress={handleEmail}
        />
      </Stack>
      <Stack style={styles.divider} />
      <Stack style={styles.section}>
        <Text
          fontWeight={isTablet ? undefined : '800'}
          variant="Font Montserrat/Subtitle/Minimize 1 Bold 11px"
          style={styles.sectionTitle}>
          {STRINGS.player.profile.overview.contactInfoTitle}
        </Text>
        <InfoRow
          label={STRINGS.player.profile.overview.labels.parentName}
          value={parentName.toUpperCase() || '-'}
        />
        <InfoRow label={STRINGS.player.profile.overview.labels.phoneNumber} value={phone || '-'} />
        <InfoRow label={STRINGS.player.profile.overview.labels.email} value={email || '-'} />
      </Stack>
      {!isTablet && <Stack flex={1} backgroundColor={colors['2']} />}
    </Stack>
  );
}
function ActionIcon({
  label,
  icon,
  onPress,
}: {
  label: string;
  icon: React.ComponentProps<typeof SvgIcon>['name'];
  onPress?: () => void;
}) {
  const styles = useStyles();
  const { isTablet, scale } = useScaling();

  return (
    <TouchableOpacity style={styles.actionBtn} onPress={onPress}>
      <SvgIcon name={icon} size={isTablet ? scale(60) : scale(48)} color={colors.Blue} />
      <Text
        variant={isTablet ? 'Font Inter/Subtitle/Regular 14px' : 'Font Inter/Subtitle/Regular'}
        style={styles.actionLabel}>
        {label}
      </Text>
    </TouchableOpacity>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();

  return (
    <Stack style={styles.infoRow}>
      <Text
        fontFamily={isTablet ? 'Inter' : 'Montserrat'}
        fontSize={isTablet ? 11 : 10}
        fontWeight={isTablet ? '400' : '500'}
        style={styles.txtInfo}>
        {label}
      </Text>
      <Text
        variant={isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Bold 10px'}
        style={[styles.infoValue, { marginTop: scale(4) }]}>
        {value}
      </Text>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  txtInfo: {
    color: colors['6'],
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors['6'],
  },
  actionsRow: {
    marginTop: scale(16),
    marginBottom: scale(10),
    justifyContent: 'center',
  },
  actionBtn: {
    gap: scale(5),
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionLabel: {
    color: colors.Blue,
  },
  section: {
    paddingHorizontal: scale(18),
    paddingVertical: scale(16),
    gap: scale(24),
  },
  sectionTitle: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  infoRow: {},
  infoValue: {
    color: colors.Blue,
  },
}));
